export type RoutingMode = 'browser' | 'hash' | 'memory';

export interface RawLocation {
    pathname: string;
    search: string;
    hash: string;
}

let mode: RoutingMode = 'browser';

const defaultLocationSource = (): RawLocation => ({
    pathname: window.location.pathname,
    // eslint-disable-next-line no-restricted-syntax -- this IS the raw window.location source that getLocationSearch() and friends are built on; routing them through the helper would recurse.
    search: window.location.search,
    hash: window.location.hash,
});

let locationSource: () => RawLocation = defaultLocationSource;

/**
 * Single source of truth for the routing mode, consulted by the URL helpers in
 * routing.utils and set once at init by the app layer (apps/websdk/src/init.*):
 * 'hash' or 'memory' on boot, 'browser' on teardown. Driven from the app rather
 * than from navigation-core, so the history lib need not import the routing barrel.
 */
export function setRoutingMode(next: RoutingMode): void {
    mode = next;
}

export function getRoutingMode(): RoutingMode {
    return mode;
}

export function isHashRouting(): boolean {
    return mode === 'hash';
}

let hashPrefix = '';

/**
 * Namespace inserted after '#' in hash mode (e.g. 'pr=' → '#pr=/path'). Set by the
 * app alongside the hash backend's prefix so the URL helpers strip the SAME marker
 * the backend writes; default '' → plain '#/path'. The backend (navigation-core)
 * keeps its own copy because it cannot import this barrel.
 */
export function setHashPrefix(prefix: string): void {
    hashPrefix = prefix || '';
}

export function getHashPrefix(): string {
    return hashPrefix;
}

/**
 * The location the URL helpers read from. Defaults to window.location; the memory
 * backend overrides it so getCurrentPath / getLocationSearch / isRootLocation can
 * resolve against an in-memory stack with no window access. Passing null restores
 * the window.location default (used on teardown).
 */
export function setLocationSource(source: (() => RawLocation) | null): void {
    locationSource = source ?? defaultLocationSource;
}

export function getRawLocation(): RawLocation {
    return locationSource();
}
